import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { SaveStrategyTemplateCreatePayload, SaveStrategyTemplateModalInput } from 'src/app/services/modal/modal';
import { DEFAULT_DRAFT_TEMPLATE_COLOR, DRAFT_TEMPLATE_COLOR_OPTIONS, DRAFT_TEMPLATES_CONFIG, DraftTemplateColor } from 'src/app/shared/constants';

@Component({
  selector: 'libero-save-strategy-template-modal',
  templateUrl: './save-strategy-template-modal.component.html',
  styleUrl: './save-strategy-template-modal.component.scss',
  standalone: false,
})
export class SaveStrategyTemplateModalComponent implements OnChanges {
  @Input() public show: SaveStrategyTemplateModalInput | null = null;
  @Input() public saving = false;
  @Output() public backBtnClick = new EventEmitter<void>();
  @Output() public createBtnClick = new EventEmitter<SaveStrategyTemplateCreatePayload>();

  private readonly nameMinLength = DRAFT_TEMPLATES_CONFIG.nameMinLength;
  private readonly nameMaxLength = DRAFT_TEMPLATES_CONFIG.nameMaxLength;
  public readonly descriptionMaxLength = DRAFT_TEMPLATES_CONFIG.descriptionMaxLength;
  private readonly defaultName = DRAFT_TEMPLATES_CONFIG.defaultName;

  public readonly colorOptions = DRAFT_TEMPLATE_COLOR_OPTIONS;

  public nameControl = new FormControl(this.defaultName, [
    Validators.required,
    Validators.minLength(this.nameMinLength),
    Validators.maxLength(this.nameMaxLength),
  ]);

  public colorControl = new FormControl(DEFAULT_DRAFT_TEMPLATE_COLOR, [Validators.required]);

  public descriptionControl = new FormControl('', [Validators.maxLength(this.descriptionMaxLength)]);

  public descriptionExpanded = false;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['show']?.currentValue) {
      this.resetForm();
    }
  }

  public toggleDescription(): void {
    this.descriptionExpanded = !this.descriptionExpanded;
  }

  public selectColor(color: DraftTemplateColor): void {
    this.colorControl.setValue(color);
    this.colorControl.markAsDirty();
  }

  public isColorSelected(color: DraftTemplateColor): boolean {
    return this.colorControl.value === color;
  }

  public onDescriptionInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.descriptionControl.setValue(textarea.value);
    this.descriptionControl.markAsDirty();
    this.descriptionControl.markAsTouched();
  }

  public get nameInvalidText(): string {
    if (this.nameControl.hasError('required')) {
      return 'draftTemplate.nameRequired';
    }
    if (this.nameControl.hasError('minlength')) {
      return 'draftTemplate.nameMinLength';
    }
    if (this.nameControl.hasError('maxlength')) {
      return 'draftTemplate.nameMaxLength';
    }
    return '';
  }

  public onCreateTemplate(): void {
    this.nameControl.markAsTouched();
    this.descriptionControl.markAsTouched();

    if (!this.show) {
      return;
    }

    this.createBtnClick.emit({
      name: this.nameControl.value ?? '',
      color: this.colorControl.value ?? '',
      description: this.descriptionControl.value || null,
    });
  }

  private resetForm(): void {
    this.nameControl.reset(this.defaultName);
    this.colorControl.setValue(DEFAULT_DRAFT_TEMPLATE_COLOR);
    this.descriptionControl.reset('');
    this.descriptionExpanded = false;
  }
}
